import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BlogItem, fetchBlogs as fetchBlogsList, getBlogSlug } from './blogApi';
import { getOrganizationContext, withOrganizationSlug } from './orgContext';

const Blogs: React.FC = () => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [blogs, setBlogs] = useState<BlogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedBlog, setSelectedBlog] = useState<BlogItem | null>(null);
  const organizationContext = getOrganizationContext();
  const organizationDisplayName = organizationContext?.organizationName || organizationContext?.organizationSlug || 'Exam Masters';
  const learningPathTitle = organizationContext
    ? `${organizationDisplayName} Courses & Practice`
    : 'Courses & Practice';

  useEffect(() => {
    loadBlogs();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // On small screens, start with the list visible and only show details after a user selects a blog.
    // This also prevents "Back" from immediately re-selecting the first post.
    if (isMobile) {
      return;
    }

    if (!selectedBlog && blogs.length > 0) {
      setSelectedBlog(blogs[0]);
    }
  }, [blogs, selectedBlog, isMobile]);

  const loadBlogs = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchBlogsList();
      setBlogs(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      setBlogs([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredBlogs = blogs.filter((blog) => {
    const normalizedSearch = searchTerm.toLowerCase();
    return (
      blog.infoName.toLowerCase().includes(normalizedSearch) ||
      blog.infoHeader.toLowerCase().includes(normalizedSearch) ||
      blog.inforDetails.toLowerCase().includes(normalizedSearch)
    );
  });

  const stripHtml = (html: string) => {
    const temp = document.createElement('div');
    temp.innerHTML = html;
    return temp.textContent || temp.innerText || '';
  };

  const truncateText = (text: string, maxLength: number = 200) => {
    const stripped = stripHtml(text);
    return stripped.length > maxLength ? `${stripped.substring(0, maxLength)}...` : stripped;
  };

  const handleCardClick = (blog: BlogItem) => {
    if (isMobile) {
      navigate(`/blogs/${getBlogSlug(blog)}`);
      return;
    }

    setSelectedBlog(blog);
  };

  const goBackToList = () => {
    setSelectedBlog(null);
  };

  const featuredCourses = [
    {
      title: `${organizationDisplayName} Courses`,
      subtitle: `Browse course pathways tailored for ${organizationDisplayName}.`,
      cta: 'Explore Course',
      path: '/courses',
      accent: 'border-emerald-200 bg-emerald-50',
    },
    {
      title: `${organizationDisplayName} Practice`,
      subtitle: `Work through topic practice matched to ${organizationDisplayName}.`,
      cta: 'See Programs',
      path: '/practice',
      accent: 'border-indigo-200 bg-indigo-50',
    },
    {
      title: `${organizationDisplayName} Exams`,
      subtitle: `Review exam-style questions and timed tests for ${organizationDisplayName}.`,
      cta: 'View Details',
      path: '/practice-tests',
      accent: 'border-amber-200 bg-amber-50',
    },
  ];

  const resolveImageUrl = (rawUrl: string | null | undefined) => {
    const trimmed = (rawUrl ?? '').trim();
    if (!trimmed) {
      return '';
    }

    if (/^https?:\/\//i.test(trimmed)) {
      return trimmed;
    }

    return `https://admn.terasite.com/${trimmed}`;
  };

  if (loading) {
    return (
      <div
        className="bg-gradient-to-br from-app-page-from to-app-page-to"
        style={{
          width: '100%',
          height: '100vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div className="app-breadcrumb-bar">
          <div className="app-breadcrumb-row">
            <button onClick={() => navigate('/')} className="app-breadcrumb-link">
              Home
            </button>
            <span className="app-breadcrumb-sep">›</span>
            <span className="app-breadcrumb-current">Blogs</span>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-3 pb-4 md:p-4 md:pb-4 lg:p-6 lg:pb-6">
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="p-4 md:p-6 bg-gradient-to-r from-blue-500 to-blue-600 text-white">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center text-lg shrink-0">
                  📰
                </div>
                <div className="min-w-0 text-left">
                  <h1 className="text-xl md:text-2xl font-bold leading-snug">Blogs</h1>
                  <p className="text-app-header-muted text-xs md:text-sm mt-0.5">
                    Latest updates and articles
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 md:p-8">
              <div className="flex items-center justify-center min-h-[240px]">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading...</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error && blogs.length === 0) {
    return (
      <div
        className="bg-gradient-to-br from-app-page-from to-app-page-to"
        style={{
          width: '100%',
          height: '100vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div className="app-breadcrumb-bar">
          <div className="app-breadcrumb-row">
            <button onClick={() => navigate('/')} className="app-breadcrumb-link">
              Home
            </button>
            <span className="app-breadcrumb-sep">›</span>
            <span className="app-breadcrumb-current">Blogs</span>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-3 pb-4 md:p-4 md:pb-4 lg:p-6 lg:pb-6">
          <div className="mb-3 md:mb-4 bg-red-50 border border-red-200 rounded-lg p-3 md:p-4">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="bg-gradient-to-br from-app-page-from to-app-page-to"
      style={{
        width: '100%',
        height: '100vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div className="app-breadcrumb-bar">
        <div className="app-breadcrumb-row">
          <button onClick={() => navigate('/')} className="app-breadcrumb-link">
            Home
          </button>
          <span className="app-breadcrumb-sep">›</span>
          <span className="app-breadcrumb-current">Blogs</span>
        </div>
      </div>

      <div className="flex-1 overflow-hidden p-3 pb-4 md:p-4 md:pb-4 lg:p-6 lg:pb-6">
        {error && blogs.length > 0 && (
          <div className="mb-3 md:mb-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3 md:p-4">
            <p className="text-yellow-700 text-sm">{error}</p>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-md overflow-hidden h-full flex flex-col min-h-0">
          <div className="flex-1 min-h-0 p-4 md:p-6">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6 h-full min-h-0">
              {/* Left: Posts list */}
              <div className={`${selectedBlog ? 'hidden md:block' : 'block'} md:col-span-4 xl:col-span-3 min-h-0`}>
                <div className="bg-white rounded-lg shadow-md overflow-hidden h-full flex flex-col min-h-0">
                  <div className="px-3 py-2.5 md:px-4 md:py-3 border-b border-gray-100 bg-gray-50">
                    <div className="flex items-start gap-3 mb-2">
                      <div className="w-9 h-9 rounded-lg bg-indigo-100 flex items-center justify-center text-lg shrink-0">
                        📰
                      </div>
                      <div className="min-w-0 text-left">
                        <p className="text-sm font-bold text-gray-900 leading-tight">Blogs</p>
                        <p className="text-xs font-semibold text-gray-500 mt-0.5">
                          {filteredBlogs.length} post{filteredBlogs.length !== 1 ? 's' : ''} available
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="p-3 md:p-4 border-b border-gray-100">
                    <div className="relative w-full">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 104.5 4.5a7.5 7.5 0 0012.15 12.15z"
                          />
                        </svg>
                      </span>
                      <input
                        type="text"
                        placeholder="Search blogs..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full rounded-lg border border-gray-200 bg-white pl-10 pr-3 py-2 text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                      />
                    </div>
                  </div>

                  <div className="flex-1 min-h-0 overflow-y-auto divide-y divide-gray-100">
                    {filteredBlogs.map((blog) => {
                      const isSelected = selectedBlog?.id === blog.id;
                      return (
                        <button
                          key={blog.id}
                          type="button"
                          onClick={() => handleCardClick(blog)}
                          className={`w-full text-left p-3 md:p-4 transition-colors ${
                            isSelected ? 'bg-indigo-50' : 'bg-white hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p
                                className={`text-sm font-semibold truncate ${
                                  isSelected ? 'text-indigo-900' : 'text-gray-900'
                                }`}
                              >
                                {blog.infoName}
                              </p>
                              <p className="mt-0.5 text-xs text-gray-600 truncate">{blog.infoHeader}</p>
                              <p className="mt-1 text-xs text-gray-500 truncate">
                                {truncateText(blog.inforDetails, 90)}
                              </p>
                              <p className="mt-1 text-[11px] text-gray-400">
                                {new Date(blog.createdDate).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                })}
                              </p>
                            </div>
                            <span className="shrink-0 text-gray-300 font-bold">›</span>
                          </div>
                        </button>
                      );
                    })}

                    {filteredBlogs.length === 0 && (
                      <div className="p-6 text-left">
                        <div className="text-3xl mb-3">🔍</div>
                        <p className="text-sm font-semibold text-gray-800">No posts found</p>
                        <p className="text-xs text-gray-500 mt-1">Try adjusting your search term.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Right: Details */}
              <div className={`${!selectedBlog ? 'hidden md:block' : 'block'} md:col-span-8 xl:col-span-6 min-h-0`}>
                <div className="bg-white rounded-lg shadow-md overflow-hidden h-full flex flex-col min-h-0">
                  <div className="px-4 py-2.5 md:px-6 md:py-3 border-b border-white/15 bg-gradient-to-r from-blue-500 to-blue-600 text-white flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                      {selectedBlog && (
                        <button
                          type="button"
                          onClick={goBackToList}
                          className="md:hidden inline-flex items-center justify-center rounded-lg border border-white/30 bg-white/10 px-2.5 py-2 text-white hover:bg-white/15"
                          aria-label="Back"
                          title="Back"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4"
                            fill="none"
                            viewBox="0 0 20 20"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M13 6l-5 4 5 4" />
                          </svg>
                        </button>
                      )}

                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center text-base shrink-0">
                          📰
                        </div>
                        <div className="min-w-0 text-left">
                          <p className="text-sm font-semibold leading-tight truncate">Blogs</p>
                          <p className="text-xs leading-tight text-white/80 truncate">Latest updates and articles</p>
                        </div>
                      </div>
                    </div>

                    {selectedBlog && (
                      <span className="shrink-0 text-xs text-white/85">
                        {new Date(selectedBlog.createdDate).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </span>
                    )}
                  </div>

                  {selectedBlog ? (
                    <>
                      <div className="flex-1 min-h-0 overflow-y-auto p-4 md:p-6">
                        <div className="min-h-full flex flex-col">
                          <h2 className="text-lg md:text-xl font-bold text-gray-900 leading-snug text-left">
                            {selectedBlog.infoName}
                          </h2>
                          <div className="mt-2 flex items-center gap-3 flex-wrap">
                            <p className="text-sm text-gray-600 text-left">{selectedBlog.infoHeader}</p>
                            <button
                              type="button"
                              onClick={() => navigate(`/blogs/${getBlogSlug(selectedBlog)}`)}
                              className="shrink-0 inline-flex items-center rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700"
                            >
                              Read full article →
                            </button>
                          </div>

                          {selectedBlog.mainimage && selectedBlog.mainimage.trim() !== '' && (
                            <div className="mt-4">
                              <img
                                src={resolveImageUrl(selectedBlog.mainimage)}
                                alt={selectedBlog.infoName}
                                className="w-full max-h-[420px] object-cover rounded-xl"
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                  if (e.currentTarget.parentElement) {
                                    e.currentTarget.parentElement.style.display = 'none';
                                  }
                                }}
                              />
                            </div>
                          )}

                          <div
                            className="blog-content mt-5 text-left"
                            dangerouslySetInnerHTML={{ __html: selectedBlog.inforDetails }}
                          />

                          <style>{`
                            .blog-content {
                              text-align: left !important;
                            }

                            .blog-content h1, .blog-content h2, .blog-content h3,
                            .blog-content h4, .blog-content h5, .blog-content h6,
                            .blog-content p,
                            .blog-content li,
                            .blog-content dt,
                            .blog-content dd,
                            .blog-content blockquote {
                              text-align: left !important;
                            }

                            .blog-content ul {
                              margin: 16px 0 !important;
                              padding-left: 24px !important;
                              list-style-type: disc !important;
                            }
                            
                            .blog-content ul ul {
                              margin: 8px 0 !important;
                              padding-left: 20px !important;
                              list-style-type: circle !important;
                            }
                            
                            .blog-content ol {
                              margin: 16px 0 !important;
                              padding-left: 24px !important;
                              list-style-type: decimal !important;
                            }
                            
                            .blog-content li {
                              margin: 8px 0 !important;
                              padding-left: 4px !important;
                              line-height: 1.6 !important;
                            }
                            
                            .blog-content li p {
                              margin: 4px 0 !important;
                            }

                            .blog-content dl {
                              margin: 20px 0 !important;
                              padding: 16px !important;
                              background-color: #f8f9fa !important;
                              border-radius: 8px !important;
                              border-left: 4px solid #2c5aa0 !important;
                            }

                            .blog-content dt {
                              font-weight: 600 !important;
                              color: #2c5aa0 !important;
                              margin: 12px 0 4px 0 !important;
                              font-size: 1rem !important;
                              display: inline-block !important;
                              min-width: 120px !important;
                            }

                            .blog-content dd {
                              margin: 0 0 12px 0 !important;
                              padding-left: 8px !important;
                              color: #333 !important;
                              font-size: 1rem !important;
                              display: inline-block !important;
                              margin-left: 8px !important;
                            }

                            .blog-content dt:first-of-type {
                              margin-top: 0 !important;
                            }

                            .blog-content dd:last-of-type {
                              margin-bottom: 0 !important;
                            }

                            .blog-content h1, .blog-content h2, .blog-content h3,
                            .blog-content h4, .blog-content h5, .blog-content h6 {
                              margin: 24px 0 16px 0 !important;
                              font-weight: 600 !important;
                              color: #2c5aa0 !important;
                            }

                            .blog-content p {
                              margin: 12px 0 !important;
                              line-height: 1.7 !important;
                              color: #374151 !important;
                            }

                            .blog-content blockquote {
                              margin: 16px 0 !important;
                              padding: 16px 20px !important;
                              border-left: 4px solid #2c5aa0 !important;
                              background-color: #f8f9fa !important;
                              font-style: italic !important;
                            }

                            .blog-content code {
                              background-color: #f1f1f1 !important;
                              padding: 2px 6px !important;
                              border-radius: 4px !important;
                              font-family: 'Courier New', monospace !important;
                              font-size: 0.9em !important;
                            }

                            .blog-content pre {
                              background-color: #f8f9fa !important;
                              padding: 16px !important;
                              border-radius: 8px !important;
                              overflow-x: auto !important;
                              margin: 16px 0 !important;
                            }

                            .blog-content a {
                              color: #2c5aa0 !important;
                              text-decoration: underline !important;
                            }

                            .blog-content a:hover {
                              color: #1e3d72 !important;
                            }

                            .blog-content strong, .blog-content b {
                              font-weight: 600 !important;
                            }

                            .blog-content em, .blog-content i {
                              font-style: italic !important;
                            }

                            .blog-content table {
                              width: 100% !important;
                              border-collapse: collapse !important;
                              margin: 16px 0 !important;
                              background-color: #fff !important;
                              box-shadow: 0 1px 3px rgba(0,0,0,0.1) !important;
                            }

                            .blog-content th, .blog-content td {
                              padding: 12px !important;
                              text-align: left !important;
                              border-bottom: 1px solid #e0e0e0 !important;
                            }

                            .blog-content th {
                              background-color: #f8f9fa !important;
                              font-weight: 600 !important;
                              color: #2c5aa0 !important;
                            }

                            .blog-content tr:hover {
                              background-color: #f8f9fa !important;
                            }
                          `}</style>

                          <div className="mt-auto pt-6">
                            {selectedBlog.tags && (
                              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 md:p-4">
                                <p className="text-xs font-semibold text-slate-600 mb-2">Related Topics</p>
                                <div className="flex flex-wrap gap-2">
                                  {selectedBlog.tags.split(',').map((tag, index) => (
                                    <span
                                      key={index}
                                      className="inline-flex items-center rounded-full bg-blue-600 px-3 py-1 text-xs font-semibold text-white"
                                    >
                                      #{tag.trim()}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}

                            <div
                              className={`${selectedBlog.tags ? 'mt-6' : ''} grid grid-cols-3 gap-2 md:gap-3 text-[11px] md:text-xs text-gray-600`}
                            >
                              <div className="rounded-lg border border-blue-200 bg-blue-50 p-2.5 md:p-3 min-w-0">
                                <p className="font-semibold text-blue-900">Published</p>
                                <p className="mt-0.5 font-semibold text-blue-800">
                                  {new Date(selectedBlog.createdDate).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                  })}
                                </p>
                              </div>
                              <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-2.5 md:p-3 min-w-0">
                                <p className="font-semibold text-indigo-900">Blog ID</p>
                                <p className="mt-0.5 font-semibold text-indigo-800">#{selectedBlog.id}</p>
                              </div>
                              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-2.5 md:p-3 min-w-0">
                                <p className="font-semibold text-emerald-900">Category</p>
                                <p className="mt-0.5 font-semibold text-emerald-800">{selectedBlog.infoType}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="flex-1 min-h-0 flex items-center justify-center p-8 text-center">
                      <div>
                        <div className="text-5xl mb-4">📰</div>
                        <h3 className="text-lg font-semibold text-gray-800 mb-1">Pick a post to begin</h3>
                        <p className="text-sm text-gray-500 max-w-sm">Select an item from the list to view details here.</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Desktop Sidebar: Courses + quick actions */}
              <div className="hidden xl:block xl:col-span-3 min-h-0">
                <div className="bg-white rounded-lg shadow-md overflow-hidden h-full flex flex-col min-h-0">
                  <div className="px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-slate-100 to-slate-50">
                    <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Learning Path</p>
                    <p className="text-sm font-semibold text-slate-900 mt-0.5">{learningPathTitle}</p>
                  </div>

                  <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-3">
                    {featuredCourses.map((course) => (
                      <div key={course.title} className={`rounded-lg border p-3 ${course.accent}`}>
                        <p className="text-sm font-semibold text-slate-900 text-left">{course.title}</p>
                        <p className="mt-1 text-xs text-slate-600 text-left">{course.subtitle}</p>
                        <button
                          type="button"
                          onClick={() => navigate(withOrganizationSlug(course.path))}
                          className="mt-2 inline-flex items-center rounded-md bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-800"
                        >
                          {course.cta}
                        </button>
                      </div>
                    ))}

                    <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
                      <p className="text-sm font-bold text-blue-900 text-left">Practice Questions</p>
                      <p className="mt-1 text-xs text-blue-700 text-left">Solve topic-wise questions with instant explanations.</p>
                      <button
                        type="button"
                        onClick={() => navigate(withOrganizationSlug('/practice'))}
                        className="mt-3 w-full inline-flex items-center justify-center rounded-lg bg-blue-600 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-700"
                      >
                        Go to Questions
                      </button>
                    </div>

                    <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-4">
                      <p className="text-sm font-bold text-indigo-900 text-left">Exam Practice</p>
                      <p className="mt-1 text-xs text-indigo-700 text-left">Take timed tests and review performance trends.</p>
                      <button
                        type="button"
                        onClick={() => navigate(withOrganizationSlug('/practice-tests'))}
                        className="mt-3 w-full inline-flex items-center justify-center rounded-lg bg-indigo-600 px-3 py-2 text-xs font-semibold text-white hover:bg-indigo-700"
                      >
                        Go to Exams
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Blogs;